import { isThemeSetting } from '@/theme/themeScript';
import { useTheme } from '@/theme/useTheme';
import type { ThemeSetting } from '@/theme/themeTypes';

import styles from './ThemeSelector.module.css';

const THEME_OPTIONS: Array<{
  description: string;
  label: string;
  value: ThemeSetting;
}> = [
  {
    description: 'Bright surfaces for daytime study.',
    label: 'Light',
    value: 'light',
  },
  {
    description: 'Low-light surfaces for evening work.',
    label: 'Dark',
    value: 'dark',
  },
  {
    description: "Follows your device's appearance setting.",
    label: 'System',
    value: 'system',
  },
];

export function ThemeSelector() {
  const {
    isSavingTheme,
    lastSavedTheme,
    setThemeSetting,
    themeSaveError,
    themeSetting,
  } = useTheme();

  function handleChange(value: string) {
    if (!isThemeSetting(value) || value === themeSetting) {
      return;
    }

    setThemeSetting(value);
  }

  const statusMessage = themeSaveError
    ? themeSaveError.message || 'Unable to save display preference.'
    : isSavingTheme
      ? 'Saving display preference...'
      : lastSavedTheme === themeSetting
        ? 'Display preference saved.'
        : '';

  return (
    <fieldset className={styles.fieldset}>
      <legend className={styles.legend}>Display theme</legend>
      <div className={styles.options}>
        {THEME_OPTIONS.map((option) => (
          <label className={styles.option} key={option.value}>
            <input
              aria-label={option.label}
              checked={themeSetting === option.value}
              className={styles.input}
              name="display-theme"
              onChange={(event) => handleChange(event.target.value)}
              type="radio"
              value={option.value}
            />
            <span className={styles.optionContent}>
              <span className={styles.optionLabel}>{option.label}</span>
              <span className={styles.optionDescription}>
                {option.description}
              </span>
            </span>
          </label>
        ))}
      </div>
      <p aria-live="polite" className={styles.status}>
        {statusMessage}
      </p>
    </fieldset>
  );
}
